import { Preset } from '@primeuix/themes/types';
import semanticCommonTokens from './one-ds-tokens/OneDS-Semantic-Common.json';
import semanticColorSchemeTokens from './one-ds-tokens/OneDS-Semantic-Color-Scheme.json';
import customTokens from './one-ds-tokens/OneDS-Custom.json';
import { t, tFlat, tColorPalette } from './token-utils';
import { AccordionTelekomDT } from './components/accordion';
import { AutoCompleteTelekomDT } from './components/autocomplete';
import { AvatarTelekomDT } from './components/avatar';
import { BadgeTelekomDT } from './components/badge';
import { BreadcrumbTelekomDT } from './components/breadcrumb';
import { ButtonTelekomDT } from './components/button';
import { CarouselTelekomDT } from './components/carousel';
import { CascadeSelectTelekomDT } from './components/cascadeselect';
import { CheckboxTelekomDT } from './components/checkbox';
import { ChipTelekomDT } from './components/chip';
import { ColorPickerTelekomDT } from './components/colorpicker';
import { DataTableTelekomDT } from './components/datatable';
import { DatePickerTelekomDT } from './components/datepicker';
import { DialogTelekomDT } from './components/dialog';
import { DividerTelekomDT } from './components/divider';
import { DockTelekomDT } from './components/dock';
import { DrawerTelekomDT } from './components/drawer';
import { EditorTelekomDT } from './components/editor';
import { FieldsetTelekomDT } from './components/fieldset';
import { FileUploadTelekomDT } from './components/fileupload';
import { FloatLabelTelekomDT } from './components/floatlabel';
import { GalleriaTelekomDT } from './components/galleria';
import { IconFieldTelekomDT } from './components/iconfield';
import { IftaLabelTelekomDT } from './components/iftalabel';
import { ImageTelekomDT } from './components/image';
import { ImageCompareTelekomDT } from './components/imagecompare';
import { InplaceTelekomDT } from './components/inplace';
import { InputGroupTelekomDT } from './components/inputgroup';
import { InputNumberTelekomDT } from './components/inputnumber';
import { InputOtpTelekomDT } from './components/inputotp';
import { InputTextTelekomDT } from './components/inputtext';
import { KnobTelekomDT } from './components/knob';
import { ListboxTelekomDT } from './components/listbox';
import { MegaMenuTelekomDT } from './components/megamenu';
import { MenuTelekomDT } from './components/menu';
import { MenubarTelekomDT } from './components/menubar';
import { MetergroupTelekomDT } from './components/metergroup';
import { MessageTelekomDT } from './components/message';
import { MultiselectTelekomDT } from './components/multiselect';
import { OrderlistTelekomDT } from './components/orderlist';
import { OrganizationchartTelekomDT } from './components/organizationchart';
import { OverlaybadgeTelekomDT } from './components/overlaybadge';
import { PaginatorTelekomDT } from './components/paginator';
import { PanelTelekomDT } from './components/panel';
import { PanelmenuTelekomDT } from './components/panelmenu';
import { PasswordTelekomDT } from './components/password';
import { PicklistTelekomDT } from './components/picklist';
import { PopoverTelekomDT } from './components/popover';
import { ProgressbarTelekomDT } from './components/progressbar';
import { ProgressSpinnerTelekomDT } from './components/progressspinner';
import { RadiobuttonTelekomDT } from './components/radiobutton';
import { RatingTelekomDT } from './components/rating';
import { ScrollpanelTelekomDT } from './components/scrollpanel';
import { SelectTelekomDT } from './components/select';
import { SelectbuttonTelekomDT } from './components/selectbutton';
import { SkeletonTelekomDT } from './components/skeleton';
import { SliderTelekomDT } from './components/slider';
import { SpeeddialTelekomDT } from './components/speeddial';
import { SplitbuttonTelekomDT } from './components/splitbutton';
import { SplitterTelekomDT } from './components/splitter';
import { TabsTelekomDT } from './components/tabs';
import { TagTelekomDT } from './components/tag';
import { TerminalTelekomDT } from './components/terminal';
import { TextareaTelekomDT } from './components/textarea';
import { TieredMenuTelekomDT } from './components/tieredmenu';
import { TimelineTelekomDT } from './components/timeline';
import { ToastTelekomDT } from './components/toast';
import { ToggleButtonTelekomDT } from './components/togglebutton';
import { ToggleswitchTelekomDT } from './components/toggleswitch';
import { ToolbarTelekomDT } from './components/toolbar';
import { TooltipTelekomDT } from './components/tooltip';
import { TreeTelekomDT } from './components/tree';
import { TreeselectTelekomDT } from './components/treeselect';
import { TreetableTelekomDT } from './components/treetable';
import { VirtualscrollerTelekomDT } from './components/virtualscroller';
import { StepperAssessmentTelekomDT } from './components/stepper-assessment';

const sc = semanticCommonTokens;
const scs = semanticColorSchemeTokens;
const fg = customTokens.ods['core-colours'].functional;

// https://github.com/primefaces/primeuix/blob/main/packages/themes/src/presets/aura/base/index.ts#L36
export const telekomPreset: Preset = {
  primitive: {
    green: tColorPalette(fg.green),
    red: tColorPalette(fg.red),
  },
  semantic: {
    formField: {
      paddingX: t(sc.form.field.padding.x),
      paddingY: t(sc.form.field.padding.y),
      sm: tFlat(sc.form.field.sm),
      lg: tFlat(sc.form.field.lg),
      borderRadius: t(sc.form.field.border.radius),
    },
    focusRing: {
      ...tFlat(sc.focus.ring),
      style: 'solid',
    },
    colorScheme: {
      light: {
        primary: tFlat(sc.primary),
        surface: tFlat(scs.surface),
        highlight: tFlat(scs.highlight),
        formField: tFlat(scs.form.field),
        overlay: tFlat(scs.overlay),
        content: tFlat(scs.content),
        text: tFlat(scs.text),
        navigation: tFlat(scs.navigation),
        list: tFlat(scs.list),
        mask: tFlat(scs.mask),
      },
    },
    disabledOpacity: String(sc.disabled.opacity.$value / 100),
  },
  components: {
    accordion: AccordionTelekomDT,
    autocomplete: AutoCompleteTelekomDT,
    avatar: AvatarTelekomDT,
    badge: BadgeTelekomDT,
    breadcrumb: BreadcrumbTelekomDT,
    button: ButtonTelekomDT,
    carousel: CarouselTelekomDT,
    cascadeselect: CascadeSelectTelekomDT,
    checkbox: CheckboxTelekomDT,
    chip: ChipTelekomDT,
    colorpicker: ColorPickerTelekomDT,
    datatable: DataTableTelekomDT,
    datepicker: DatePickerTelekomDT,
    dialog: DialogTelekomDT,
    divider: DividerTelekomDT,
    dock: DockTelekomDT,
    drawer: DrawerTelekomDT,
    editor: EditorTelekomDT,
    fieldset: FieldsetTelekomDT,
    fileupload: FileUploadTelekomDT,
    floatlabel: FloatLabelTelekomDT,
    galleria: GalleriaTelekomDT,
    iconfield: IconFieldTelekomDT,
    iftalabel: IftaLabelTelekomDT,
    image: ImageTelekomDT,
    imagecompare: ImageCompareTelekomDT,
    inplace: InplaceTelekomDT,
    inputgroup: InputGroupTelekomDT,
    inputnumber: InputNumberTelekomDT,
    inputotp: InputOtpTelekomDT,
    inputtext: InputTextTelekomDT,
    knob: KnobTelekomDT,
    listbox: ListboxTelekomDT,
    megamenu: MegaMenuTelekomDT,
    menu: MenuTelekomDT,
    menubar: MenubarTelekomDT,
    metergroup: MetergroupTelekomDT,
    message: MessageTelekomDT,
    multiselect: MultiselectTelekomDT,
    orderlist: OrderlistTelekomDT,
    organizationchart: OrganizationchartTelekomDT,
    overlaybadge: OverlaybadgeTelekomDT,
    paginator: PaginatorTelekomDT,
    panel: PanelTelekomDT,
    panelmenu: PanelmenuTelekomDT,
    password: PasswordTelekomDT,
    picklist: PicklistTelekomDT,
    popover: PopoverTelekomDT,
    progressbar: ProgressbarTelekomDT,
    progressspinner: ProgressSpinnerTelekomDT,
    radiobutton: RadiobuttonTelekomDT,
    rating: RatingTelekomDT,
    scrollpanel: ScrollpanelTelekomDT,
    select: SelectTelekomDT,
    selectbutton: SelectbuttonTelekomDT,
    skeleton: SkeletonTelekomDT,
    slider: SliderTelekomDT,
    speeddial: SpeeddialTelekomDT,
    splitbutton: SplitbuttonTelekomDT,
    splitter: SplitterTelekomDT,
    stepper: StepperAssessmentTelekomDT,
    tabs: TabsTelekomDT,
    tag: TagTelekomDT,
    terminal: TerminalTelekomDT,
    textarea: TextareaTelekomDT,
    tieredmenu: TieredMenuTelekomDT,
    timeline: TimelineTelekomDT,
    toast: ToastTelekomDT,
    togglebutton: ToggleButtonTelekomDT,
    toggleswitch: ToggleswitchTelekomDT,
    toolbar: ToolbarTelekomDT,
    tooltip: TooltipTelekomDT,
    tree: TreeTelekomDT,
    treeselect: TreeselectTelekomDT,
    treetable: TreetableTelekomDT,
    virtualscroller: VirtualscrollerTelekomDT,
  },
};
